import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom-original";
import { STRINGS, LANGS } from "./strings";
import { urlLang, withLang, HTML_LANG } from "../lib/langPath";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const [stored, setStored] = useState(() => {
    const saved = localStorage.getItem("ckb_lang");
    return LANGS.some((l) => l.code === saved) ? saved : "en";
  });
  // On public pages the URL decides the language; elsewhere the saved choice does.
  const fromUrl = urlLang(pathname);
  const lang = fromUrl || stored;
  const loc = useRef({});
  loc.current = { fromUrl, pathname, search, hash };

  // Saves the preference only (used for the signed-in profile); never navigates.
  const setStoredLang = useCallback((code) => {
    if (!LANGS.some((l) => l.code === code)) return;
    localStorage.setItem("ckb_lang", code);
    setStored(code);
  }, []);

  const setLang = useCallback((code) => {
    if (!LANGS.some((l) => l.code === code)) return;
    setStoredLang(code);
    const c = loc.current;
    if (c.fromUrl) navigate(withLang(c.pathname, code) + c.search + c.hash);
  }, [setStoredLang, navigate]);

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[lang];
  }, [lang]);

  const t = useCallback((key, vars) => {
    let s = STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key;
    if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
    return s;
  }, [lang]);

  // Pick the localized value from a {en, hi, hinglish} content object.
  const L = useCallback((obj) => (obj && typeof obj === "object" ? obj[lang] || obj.en || "" : obj || ""), [lang]);

  // Overlay a content item's stored translation for the current language, if present.
  const T = useCallback((item) => {
    const tr = lang !== "en" && item?.translations?.[lang];
    return tr ? { ...item, ...tr, _translated: true } : item;
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, setStoredLang, t, L, T }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
